import { createApi, FetchArgs } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../../baseQuery/basePlatformQuery';
import { BulkDoctorsAssignPayload, CitiesRequestPayload, City, Doctor, DoctorRequestPayload, Specialty } from './types';

const BASE_URL = '/api/v1/doctors';

type EndpointName = 'doctors' | 'cities' | 'specialties' | 'assign';

const abortControllers: Record<EndpointName, AbortController> = {
    doctors: new AbortController(),
    cities: new AbortController(),
    specialties: new AbortController(),
    assign: new AbortController(),
};

const doctorsApi = createApi({
    reducerPath: 'doctorsApi',
    baseQuery: async (args: FetchArgs, api, extraOptions) => {
        const endpointName = args.url.split('/').pop() as EndpointName;
        if (abortControllers[endpointName]) {
            abortControllers[endpointName].abort();
        }

        abortControllers[endpointName] = new AbortController();
        return baseQuery({ ...args, signal: abortControllers[endpointName].signal }, api, extraOptions);
    },
    endpoints: (builder) => ({
        getDoctors: builder.query<Array<Doctor>, DoctorRequestPayload>({
            query: (body) => ({
                url: BASE_URL,
                method: 'POST',
                body: {
                    ...body,
                    states: body.states.map((state) => state.value),
                },
            }),
        }),
        getCities: builder.query<Array<City>, CitiesRequestPayload>({
            query: ({ search, pageNumber, take, states }) => {
                const params = new URLSearchParams();
                if (search) params.append('Search', search);
                if (states && states.length > 0) {
                    states.forEach((state) => params.append('States', state.value));
                }
                if (pageNumber) params.append('PageNumber', pageNumber.toString());
                if (take) params.append('PageSize', take.toString());
                return {
                    url: `${BASE_URL}/cities`,
                    params,
                };
            },
        }),
        getSpecialties: builder.query<Array<Specialty>, CitiesRequestPayload>({
            query: ({ search, pageNumber, take }) => {
                const params = new URLSearchParams();
                if (search) params.append('Search', search);
                if (pageNumber) params.append('PageNumber', pageNumber.toString());
                if (take) params.append('PageSize', take.toString());
                return {
                    url: `${BASE_URL}/specialties`,
                    params,
                };
            },
        }),
        bulkDoctorsAssign: builder.mutation<void, BulkDoctorsAssignPayload>({
            query: (body) => ({
                url: `${BASE_URL}/assign`,
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const { useGetDoctorsQuery, useGetCitiesQuery, useGetSpecialtiesQuery, useBulkDoctorsAssignMutation } =
    doctorsApi;
export default doctorsApi;
